import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const API_VERSION_V1 = 'v1'
const API_VERSION_V2 = 'v2'
const REQUEST_URL_V1 = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION_V1}/planner`
const REQUEST_URL_V2 = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION_V2}/kazoo-planner-market-place`

export const getPlanners = createAsyncThunk(
	'getPlanners',
	async (payload = {}) => {
		try {
			const res = await fetch(`${REQUEST_URL_V2}/get-kazoo-planners`, {
				method: 'POST',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			})

			const data = await res.json()
			return data
		} catch (err) {
			console.error('Error fetching planners:', err.message)
		}
	}
)

export const addPlannerToKazoo = createAsyncThunk(
	'kazoo/addPlannerToKazoo',
	async ({ kazooId, plannerId, plannerData }) => {
		try {
			// Reuse vendor API endpoint for adding planners
			const res = await fetch(`${process.env.REACT_APP_REQUEST_URL}/api/v1/vendor/add-vendor/${kazooId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ vendorId: plannerId, ...plannerData }),
			})

			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const getPlannerFieldValues = createAsyncThunk(
	'planner/getPlannerFieldValues',
	async (payload = {}) => {
		try {
			const queryString = new URLSearchParams(
				Object.entries(payload).filter(([_, value]) => value != null)
			).toString()

			// Reuse vendor API endpoint (vendors and planners share same data model)
			const url = `${process.env.REACT_APP_REQUEST_URL}/api/v1/vendor/field-values${queryString ? `?${queryString}` : ''}`

			const res = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

const initialState = {
	plannerSearchList: [],
	plannerSearchListLoading: false,
	plannerTotalPages: 0,
	plannerCurrentPage: 1,
	plannerTotalCount: 0,
	planners: [],
	plannerFilters: { page: 1, limit: 10 },
	plannerFieldValues: {},
}

const plannerSlice = createSlice({
	name: 'planner',
	initialState,
	reducers: {
		setPlannerFilters: (state, { payload }) => {
			state.plannerFilters = payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getPlanners.pending, (state) => {
				state.plannerSearchListLoading = true
				state.plannerSearchList = []
				state.plannerTotalPages = 0
				state.plannerCurrentPage = 1
				state.plannerTotalCount = 0
			})
			.addCase(getPlanners.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to fetch for planners',
						'error'
					)
					state.plannerSearchListLoading = false
					return
				}
				state.plannerSearchListLoading = false
				state.plannerSearchList= payload.planners
				state.plannerTotalPages = payload.totalPages
				state.plannerCurrentPage = payload.currentPage
				state.plannerTotalCount = payload.totalCount
			})
			.addCase(getPlanners.rejected, (state) => {
				state.plannerSearchListLoading = false
				state.plannerSearchList = []
				state.plannerTotalPages = 0
				state.plannerCurrentPage = 1
				state.plannerTotalCount = 0
			})

			.addCase(addPlannerToKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to add planner to wedding',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Successfully added planner to wedding',
					'success'
				)
				state.planners = [...state.planners, payload.planner]
			})

			.addCase(getPlannerFieldValues.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch planner field values',
						'error'
					)
					return
				}
				state.plannerFieldValues = {
					...state.plannerFieldValues,
					[payload.fieldName]: payload.values,
				}
			})
	},
})

export const { setPlannerFilters } = plannerSlice.actions

export default plannerSlice.reducer
